import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import LoadingButton from '@mui/lab/LoadingButton'
import Tooltip from '@mui/material/Tooltip'
import { LOADING } from '../../constants/common'
import { useAuth } from '../../contexts/Auth'
import paths from '../../constants/paths'

const PasswordRecovery = ({ resend, email }) => {
  const { forgetPasswordStatus, prev } = useAuth();
  const [remainingTime, setRemainingTime] = useState(60);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const savedTime = localStorage.getItem('timerStart');
    if (savedTime) {
      const timeElapsed = Math.floor((Date.now() - parseInt(savedTime, 10)) / 1000);
      if (timeElapsed < 60) {
        setIsDisabled(true);
        setRemainingTime(60 - timeElapsed);
      } else {
        setIsDisabled(false);
      }
    }
  }, []);

  useEffect(() => {
    let timerInterval;

    if (isDisabled) {
      timerInterval = setInterval(() => {
        const savedTime = localStorage.getItem('timerStart');
        const timeElapsed = Math.floor((Date.now() - parseInt(savedTime, 10)) / 1000);

        if (timeElapsed >= 60) {
          setIsDisabled(false);
          setIsLoading(false);
          localStorage.removeItem('timerStart');
          localStorage.removeItem('email');
          localStorage.removeItem('resetStep');
          clearInterval(timerInterval);
        } else {
          setRemainingTime(60 - timeElapsed);
        }
      }, 1000);
    }

    return () => clearInterval(timerInterval);
  }, [isDisabled]);

  useEffect(() => {
    if (forgetPasswordStatus === 'SUCCESS') {
      setIsDisabled(true);
      setRemainingTime(60);
      setIsLoading(false);
    } else if (forgetPasswordStatus === LOADING) {
      setIsLoading(true);
    } else if (forgetPasswordStatus === 'ERROR' || forgetPasswordStatus === 'IDLE') {
      setIsLoading(false);
    }
  }, [forgetPasswordStatus])

  const handleResendClick = () => {
    if (!isDisabled && !isLoading) {
      setIsLoading(true);
      resend();
    }
  };

  const DisabledLinkWithTooltip = () => (
    <Tooltip 
      title={isLoading ? "Sending email..." : `Please wait for ${remainingTime} seconds before resending email`}
      enterDelay={0}
    >
      <Link
        component="span"
        sx={{
          cursor: 'default',
          color: 'text.disabled',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'none'
          }
        }}
      >
        resending the email
      </Link>
    </Tooltip>
  );

  const EnabledLink = () => (
    <Link
      onClick={handleResendClick}
      sx={{
        cursor: 'pointer',
        '&:hover': {
          textDecoration: 'underline'
        }
      }}
    >
      resending the email
    </Link>
  );

  const isLinkDisabled = isDisabled || isLoading;

  return (
    <Box
      sx={{
        maxWidth: 600,
        px: 2,
        mx: 'auto'
      }}
    >
      <Paper
        sx={{
          p: 4,
          boxShadow: '0px 0px 64px rgba(0, 0, 0, 0.04)',
          border: 0
        }}
      >
        <Typography variant='h3' mb={3}>
          Password Recovery
        </Typography>

        <Typography variant='body1' mb={4}>
          We've sent an email to {email} with instructions to reset your password.
        </Typography>

        <Typography variant='body1' mb={4}>
          If you don't see the email within a few minutes, please check your junk/spam folder or try {isLinkDisabled ? <DisabledLinkWithTooltip /> : <EnabledLink />}.
        </Typography>

        <Typography variant='body1' mb={4}>
          Still having trouble or unsure which email is linked to your account? <Link href='mailto:team@therapywordofmouth.com'>Contact us</Link> for support.
        </Typography>

        <Button
          fullWidth
          variant='contained'
          size='large'
          sx={{ mb: 1 }}
          href={paths.login}
          onClick={prev}
        >
          Go back to login
        </Button>
        <LoadingButton
          fullWidth
          size='large'
          onClick={handleResendClick}
          disabled={isLinkDisabled}
          loading={forgetPasswordStatus === LOADING}
        >
          {isDisabled ? `Retry after ${remainingTime}s` : 'Resend password reset email'}
        </LoadingButton>
      </Paper>
    </Box>
  )
}

export default PasswordRecovery